import React, { useMemo } from "react";
import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { format, parseISO } from "date-fns";
import { type Lam2ShiftRollsRow } from "../../../api/lam2";

/**
 * Stacked A/B/F roll counts per shift, shared by the Lamination 2 Rolls tab and
 * the Operations Overview card.
 *
 * Counts come from the PLC, which classifies each roll itself — the in-progress
 * shift is drawn faded so a short bar isn't mistaken for a bad shift.
 */

export const ROLL_COLORS = {
  a: "#2e7d32", // green — good
  b: "#f9a825", // amber — second quality
  f: "#d32f2f", // red — fail / off-spec
};

/**
 * Shift window for the overview cards. The bar and the mix-trend chart read as
 * one story only if they cover the same shifts, so they share this.
 */
export const OVERVIEW_SHIFTS = 30;

/**
 * Axis geometry shared by both overview charts, so their plot areas start at
 * the same x and a given shift sits at the same place in each.
 * The width fits a "100%" tick, the widest label either axis draws.
 */
export const AXIS_WIDTH = 48;
export const CHART_MARGIN = { top: 8, right: 16, bottom: 28, left: 8 };

/**
 * Both shift charts draw every tick (interval={0}) rather than letting recharts
 * thin them. Its thinning drops every other tick, which on a Day/Night
 * schedule silently removes one shift type — the axis then reads as if the
 * line only ran days. Density is handled by shrinking the labels instead; see
 * `renderShiftTick`.
 */

type Row = Lam2ShiftRollsRow & { label: string; sublabel: string };

export const fmtRolls = (n: number) => new Intl.NumberFormat().format(n || 0);

/** Share of a whole, e.g. 740 of 863 -> "85.7%". Empty when there's nothing to divide by. */
export const fmtShare = (part: number, whole: number) =>
  whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : "—";

/**
 * True for the backend's "no matching row in settings_shifts" label — a bare
 * "20:00" rather than a configured shift name.
 */
const isFallbackShiftName = (name: string) => /^\d{1,2}:\d{2}$/.test(name);

/**
 * "7 shifts" — how many a bucket holds, which the day/week charts put in this
 * slot instead of a shift name. Taking its first word would leave a bare "7".
 */
const isShiftCount = (name: string) => /^\d+\s+shifts?$/.test(name);

/**
 * Shorten a shift for tight axes to its initial: "Night Shift" -> "N",
 * "Lamination 1 Day Shift" -> "D". An hour fallback like "16:00" (a start no
 * configured shift matches) is placed by the plant's 06:00/18:00 changeover.
 * Bucket counts like "7 shifts" are not names and are left whole.
 */
const shortShiftName = (name: string) => {
  if (isShiftCount(name)) return name;
  if (isFallbackShiftName(name)) {
    const hour = Number(name.split(":")[0]);
    return hour >= 6 && hour < 18 ? "D" : "N";
  }
  if (/\bnight\b/i.test(name)) return "N";
  if (/\bday\b/i.test(name)) return "D";
  return name.split(/\s+/)[0];
};

/**
 * Two-line tick: date on top, which shift beneath.
 *
 * With every tick drawn, a day's shifts repeat the same date under each bar,
 * so `dedupeDates` prints it once per day and lets the shift line carry the
 * rest. It must stay off while recharts is thinning ticks — the previous row
 * is then a tick nobody drew, and suppressing against it would leave a bar
 * with no date at all.
 */
export const renderShiftTick = (
  rows: Array<{ label: string; sublabel: string }>,
  short: boolean,
  dedupeDates = false,
  /** Shrink the type so every shift still fits when ticks are packed. */
  dense = false
) => {
  // A date under every slot overlaps once a month of them is packed into a
  // half-width card, so only every k-th date is printed — enough to read the
  // axis by. Week labels ("Aug 9 – Aug 15") are about twice as wide.
  const dates = rows.filter((r, i) => i === 0 || rows[i - 1].label !== r.label);
  const wide = rows.some((r) => r.label.includes("–"));
  const maxDates = (short ? 7 : 14) / (wide ? 2 : 1);
  const every = Math.max(1, Math.ceil(dates.length / maxDates));
  const shownDates = new Set(
    dates.filter((_, i) => i % every === 0).map((r) => r.label)
  );

  return ({ x, y, payload }: any) => {
    const i = payload?.index ?? -1;
    const row = rows[i];
    if (!row) return <g />;
    const firstOfDate = !dedupeDates || i === 0 || rows[i - 1]?.label !== row.label;
    const showDate = firstOfDate && shownDates.has(row.label);
    // A shift count ("2 shifts") is as wide as a date; packed, it is left to the
    // tooltip rather than drawn over its neighbours.
    const sub =
      short || dense
        ? dense && isShiftCount(row.sublabel)
          ? ""
          : shortShiftName(row.sublabel)
        : row.sublabel;
    return (
      <g transform={`translate(${x},${y})`}>
        <text dy={12} textAnchor="middle" fill="#666" fontSize={dense ? 10 : 11}>
          {showDate ? row.label : ""}
        </text>
        {/* Every shift keeps its initial — Day and Night both have to be
            readable, so a packed axis shrinks the type instead of dropping
            half the labels. */}
        <text dy={24} textAnchor="middle" fill="#999" fontSize={dense ? 9 : 10}>
          {sub}
        </text>
      </g>
    );
  };
};

export const RollsLegend: React.FC<{ grades?: Array<"A" | "B" | "F"> }> = ({
  grades = ["A", "B", "F"],
}) => (
  <Stack direction="row" spacing={2}>
    {[
      { k: "A", c: ROLL_COLORS.a },
      { k: "B", c: ROLL_COLORS.b },
      { k: "F", c: ROLL_COLORS.f },
    ]
      .filter((s) => grades.includes(s.k as "A" | "B" | "F"))
      .map((s) => (
        <Stack key={s.k} direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{ width: 12, height: 12, borderRadius: "2px", bgcolor: s.c }}
          />
          <Typography variant="caption">{s.k}</Typography>
        </Stack>
      ))}
  </Stack>
);

/** A row may arrive already named, when the caller bucketed it. */
export type RollsChartRow = Lam2ShiftRollsRow & {
  label?: string;
  sublabel?: string;
};

/**
 * Ticks for a y axis topped at `top` (a multiple of 100): every 100 while that
 * stays readable, coarser past it, and always the top itself — Recharts' own
 * ticks on a fixed domain leave an odd last gap like 750 → 900.
 */
const hundredsTicks = (top: number): number[] => {
  const step = [100, 200, 250, 500, 1000].find((s) => top / s <= 10) ?? top / 5;
  const ticks: number[] = [];
  for (let t = 0; t < top; t += step) ticks.push(t);
  ticks.push(top);
  return ticks;
};

type Props = {
  rows: RollsChartRow[];
  height?: number;
  /** Drop the shift-name line under each tick (tight spaces). */
  compact?: boolean;
  /**
   * Fix the top of the y axis at this many rolls, so charts shown side by side
   * share a scale. Should be a multiple of 100 no lower than the tallest bar;
   * left out, the axis fits this chart's own bars.
   */
  yMax?: number;
};

export const Lam2RollsChart: React.FC<Props> = ({
  rows,
  height,
  compact = false,
  yMax,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const chartHeight = height ?? (isMobile ? 260 : 380);

  // Only shifts the PLC actually counted — idle days are left off the axis
  // rather than padded with zero-roll bars.
  const data: Row[] = useMemo(
    () =>
      rows.map((r) => {
        // A caller that has already bucketed these rows names them itself — a
        // week's "Aug 9 – Aug 15" cannot be derived from one start date here.
        if (r.label && r.sublabel) return { ...r, label: r.label, sublabel: r.sublabel };
        let label = r.shift_start;
        try {
          label = format(parseISO(r.shift_start), "MMM d");
        } catch {
          /* keep raw */
        }
        return { ...r, label, sublabel: r.shift_name };
      }),
    [rows]
  );

  // Which shift a bar belongs to always shows; only the wording shortens when
  // space is tight ("Night Shift" -> "Night").
  const short = compact || isMobile;
  // Every shift gets a tick. Recharts' own thinning skips alternate ticks,
  // which on a Day/Night axis silently hides one shift type — the labels then
  // read as if the line only ran days. Initials keep them legible when packed.
  const dense = data.length > (short ? 12 : 28);
  const interval = 0;

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={data} margin={CHART_MARGIN} barCategoryGap="20%">
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="shift_id"
          tick={renderShiftTick(data, short, true, dense)}
          height={42}
          interval={interval}
        />
        <YAxis
          tickMargin={8}
          allowDecimals={false}
          width={AXIS_WIDTH}
          domain={yMax ? [0, yMax] : undefined}
          ticks={yMax ? hundredsTicks(yMax) : undefined}
        />
        <Tooltip
          cursor={{ fill: "rgba(0,0,0,0.04)" }}
          labelFormatter={(_, payload) => {
            const r = payload?.[0]?.payload as Row | undefined;
            if (!r) return "";
            return `${r.label} · ${r.sublabel}${
              r.is_partial ? " (in progress)" : ""
            } — ${fmtRolls(r.total)} rolls`;
          }}
          // Each grade also as a share of that shift's own output.
          formatter={(v: any, name: any, item: any) => {
            const r = item?.payload as Row | undefined;
            const n = Number(v);
            const total = r?.total ?? 0;
            return [
              total > 0
                ? `${fmtRolls(n)} (${fmtShare(n, total)})`
                : fmtRolls(n),
              name,
            ];
          }}
        />
        {/* Stacked so each bar is the shift's whole output. */}
        <Bar dataKey="a_rolls" name="A" stackId="rolls" fill={ROLL_COLORS.a}>
          {data.map((r) => (
            <Cell key={r.shift_id} fillOpacity={r.is_partial ? 0.55 : 1} />
          ))}
        </Bar>
        <Bar dataKey="b_rolls" name="B" stackId="rolls" fill={ROLL_COLORS.b}>
          {data.map((r) => (
            <Cell key={r.shift_id} fillOpacity={r.is_partial ? 0.55 : 1} />
          ))}
        </Bar>
        <Bar dataKey="f_rolls" name="F" stackId="rolls" fill={ROLL_COLORS.f}>
          {data.map((r) => (
            <Cell key={r.shift_id} fillOpacity={r.is_partial ? 0.55 : 1} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default Lam2RollsChart;
